import { PatientAge } from "./interfaces";

// utils/date.ts
type YearMonthDay = { year: number; month: number; day: number };
const SPANISH_LOCALE = "es-AR";

/**
 * Convierte una fecha a formato ISO-8601 completo con hora UTC
 * Prisma requiere: "YYYY-MM-DDTHH:mm:ss.sssZ"
 * @param input - Fecha en formato DD/MM/YYYY o YYYY-MM-DD
 * @returns Fecha en formato ISO completo o undefined
 */
export function convertToISODateTime(input?: string | null): string | undefined {
  if (!input) return undefined;
  
  const isoDate = convertToISO(input); // Obtiene "YYYY-MM-DD"
  if (!isoDate) return undefined;
  
  // Agregar hora medianoche en UTC
  return `${isoDate}T00:00:00.000Z`;
}

/**
 * Convierte un string de fecha a objeto YearMonthDay
 * Soporta formatos: ISO (YYYY-MM-DD), DD/MM/YYYY
 */
export const toYearMonthDay = (
  dateString?: string | null
): YearMonthDay | null => {
  if (!dateString) return null;
  const str = dateString.trim();
  // ISO con o sin hora (YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss)
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    // Si string contiene T o espacio, es un ISO con hora creamos un objeto Date para que el navegador aplique el offset local (UTC-3).
    if (str.includes("T") || str.includes(" ")) {
      const d = new Date(str);
      if (!isNaN(d.getTime())) {
        return {
          year: d.getFullYear(),
          month: d.getMonth() + 1,
          day: d.getDate(),
        };
      }
    }

    const [year, month, day] = str.slice(0, 10).split("-").map(Number);
    return { year, month, day };
  }
  // DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split("/").map(Number);
    return { year, month, day };
  }
  return null;
};

/**
 * Obtiene la fecha actual como YearMonthDay (sin timezone shift)
 */
export const todayYearMonthDay = (): YearMonthDay => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };
};

/**
 * Valida que el dia exista para ese mes/año.
 */
export function isValidYMD(ymd: YearMonthDay): boolean {
  const candidate = new Date(ymd.year, ymd.month - 1, ymd.day);
  return (
    candidate.getFullYear() === ymd.year &&
    candidate.getMonth() + 1 === ymd.month &&
    candidate.getDate() === ymd.day
  );
}

/**
 * Calcula la edad entre una fecha de nacimiento y una fecha de referencia
 * @param birthDate - Fecha de nacimiento (DD/MM/YYYY o ISO)
 * @param referenceDate - Fecha de referencia (opcional, por defecto hoy)
 * @returns Edad en años o undefined si la fecha es inválida
 */
export const calculatePatientAge = (
  birthDate: string | undefined,
  referenceDate?: string | null
): PatientAge | undefined => {
  const birth = toYearMonthDay(birthDate);
  if (!birth || !isValidYMD(birth)) return undefined;

  const ref = referenceDate ? toYearMonthDay(referenceDate) : todayYearMonthDay();
  if (!ref || !isValidYMD(ref)) return undefined;

  const birthDateObj = new Date(birth.year, birth.month - 1, birth.day);
  const refDateObj = new Date(ref.year, ref.month - 1, ref.day);

  if (refDateObj < birthDateObj) return undefined; // fecha futura

  // Calcular diferencia básica
  let years = ref.year - birth.year;
  let months = ref.month - birth.month;
  let days = ref.day - birth.day;

  if (days < 0) {
    months--;
    const prevMonth = new Date(ref.year, ref.month - 1, 0).getDate();
    days += prevMonth;
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  let label = "";
  if (years >= 1) label = `${years} año${years > 1 ? "s" : ""}`;
  else if (months >= 1) label = `${months} mes${months > 1 ? "es" : ""}`;
  else label = `${days} día${days !== 1 ? "s" : ""}`;

  return { years, months, days, label };
};

function extractIsoYYYYMMDD(dateString: string): string | null {
  const match = dateString.trim().match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : null;
}
/**
 * Convierte una fecha a ISO (YYYY-MM-DD) de forma tolerante.
 * Acepta: ISO con/sin hora, DD/MM/YYYY, y valores parseables por Date.
 * No lanza: retorna undefined si no puede parsear.
 */
export function convertToISO(input?: string | null): string | undefined {
  if (!input) return undefined;
  const trimmed = input.trim();
  if (!trimmed) return undefined;

  //Ya es ISO (YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss…)
  const iso = extractIsoYYYYMMDD(trimmed);
  if (iso) return iso;

  // DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split("/");
    return `${year}-${month}-${day}`;
  }

  // Parseable por Date
  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return undefined;
}

/**
 * Formatea una fecha a DD/MM/YYYY
 * Soporta: ISO, DD/MM/YYYY, formatos parseables por Date
 */
export const formatToDDMMYYYY = (dateString?: string | null): string => {
  if (!dateString) return "";
  const ymd = toYearMonthDay(dateString);
  if (ymd && isValidYMD(ymd)) {
    const day = String(ymd.day).padStart(2, "0");
    const month = String(ymd.month).padStart(2, "0");
    return `${day}/${month}/${ymd.year}`;
  }
  const parsed = new Date(dateString);
  if (!isNaN(parsed.getTime())) {
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    return `${day}/${month}/${year}`;
  }
  return dateString;
};

// Fromateador para "Miércoles, 05/06/2024"
export function formatWeekdayDDMMYYYY(dateString?: string | null): string {
  const ymd = toYearMonthDay(dateString ?? "");
  if (!ymd || !isValidYMD(ymd)) return "";
  const date = new Date(ymd.year, ymd.month - 1, ymd.day);
  const text = date.toLocaleDateString(SPANISH_LOCALE, {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Hora "HH:mm" (24h).
export function formatTimeHM_TZ(
  dateString?: string | Date | null,
  timeZone: string = "America/Argentina/Buenos_Aires"
): string {
  if (!dateString) return "";
  const date = typeof dateString === "string" ? new Date(dateString) : dateString;
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  });
}

// “Lunes 23/09/24” (año en 2 dígitos)
export function formatWeekdayDDMMYY(
  dateString?: string | null,
  timeZone: string = "America/Argentina/Buenos_Aires"
): string {
  const ymd = toYearMonthDay(dateString ?? "");
  if (!ymd || !isValidYMD(ymd)) return "";
  const date = new Date(ymd.year, ymd.month - 1, ymd.day);
  const text = date.toLocaleDateString("es-AR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    timeZone,
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Formatea una fecha ISO (YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss) a DD/MM/YYYY
 * SIN aplicar conversión de zona horaria (usa la fecha literal del string).
 * Ideal para fechas que vienen del backend con el formato final.
 */
export function formatIsoDateToDDMMYYYY(isoString?: string | null): string {
  if (!isoString) return "";
  const trimmed = isoString.trim();
  
  // Extraer parte de fecha YYYY-MM-DD
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }

  // Fallback a comportamiento normal si no parece ISO
  return formatToDDMMYYYY(isoString);
}